import { AbstractControl, ValidatorFn } from '@angular/forms';

/**
 * 代碼格式驗證
 * 僅接受英文、數字、底線(_)、橫線(-)、點(.)的組合
 */
export const codeFormatValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
  if (!control.value) return null;
  const pattern = /^[a-zA-Z0-9_\-.]+$/;
  const valid = pattern.test(control.value);
  return valid ? null : { 'custom': '僅接受英文,數字,底線(_),橫線(-),點(.)的組合' };
};

/**
 * 批號格式驗證
 * 僅接受英文、數字、底線(_)、橫線(-)、點(.)、斜線(/)的組合
 * (批號專用, 較 codeFormatValidator 多允許斜線)
 */
export const batchNoFormatValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
  if (!control.value) return null;
  const pattern = /^[a-zA-Z0-9_./-]+$/;
  const valid = pattern.test(control.value);
  return valid ? null : { 'custom': '僅接受英文,數字,底線(_),橫線(-),點(.),斜線(/)的組合' };
};

/**
 * Email 格式驗證
 * 必須包含 @ 符號; @ 前後都必須有字元; 域名必須包含.字元; 不能是空白
 */
export const emailFormatValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
  if (!control.value) return null;
  const pattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const valid = pattern.test(control.value);
  return valid ? null : { 'custom': 'Email 格式不正確' };
};

/**
 * 統一編號格式驗證
 * 驗證台灣統一編號的格式與檢查碼
 */
export const uniformFormatValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
  if (!control.value) return null;

  const value = control.value.toString();

  // 檢查是否為 8 位數字
  if (!/^\d{8}$/.test(value)) {
    return { 'custom': '統一編號必須為 8 位數字' };
  }

  // 邏輯乘數
  const weights = [1, 2, 1, 2, 1, 2, 4, 1];
  let sum = 0;

  for (let i = 0; i < 8; i++) {
    const digit = parseInt(value[i], 10);
    const product = digit * weights[i];
    // 乘積的十位數 + 個位數，如果結果是 10 則視為 0
    let s = Math.floor(product / 10) + (product % 10);
    if (s === 10) s = 0;
    sum += s;
  }

  // 第 7 位（index 6）的值
  const digit7 = parseInt(value[6], 10);

  // 驗證規則：
  // 1. 乘積之和能被 5 整除
  // 2. 或當第 7 位為 7 時，(sum + 1) 能被 5 整除也算合法
  const isValid = (sum % 5 === 0) || (digit7 === 7 && (sum + 1) % 5 === 0);

  return isValid ? null : { 'custom': '統一編號格式不正確' };
};

/**
 * 發票號碼格式驗證
 * 台灣發票號碼格式：2碼英文 + 8碼數字
 */
export const invoiceNoFormatValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
  if (!control.value) return null;
  const pattern = /^[A-Za-z]{2}\d{8}$/;
  const valid = pattern.test(control.value);
  return valid ? null : { 'custom': '發票號碼格式不正確，應為 2 碼英文 + 8 碼數字' };
};

/**
 * 月結檢查（純函式）
 * 判斷指定日期所屬月份是否已月結
 * @param date 要檢查的日期
 * @param latestMonthEnded 最新已月結月份（null 表示尚未執行過月結）
 * @returns true 表示已月結
 */
export function isMonthEnded(date: Date | null, latestMonthEnded: Date | null): boolean {
  if (!date || !latestMonthEnded) {
    return false;
  }

  const selectedDate = new Date(date);
  const selectedYearMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);

  const latestDate = new Date(latestMonthEnded);
  const latestYearMonth = new Date(latestDate.getFullYear(), latestDate.getMonth(), 1);

  return selectedYearMonth <= latestYearMonth;
}

/**
 * 月結檢查驗證器
 * 檢查選擇的日期所屬月份是否已月結
 * @param latestMonthEnded 最新已月結月份（null 表示尚未執行過月結）
 */
export function monthEndedValidator(latestMonthEnded: Date | null): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value || !latestMonthEnded) {
      return null;
    }

    if (isMonthEnded(new Date(control.value), latestMonthEnded)) {
      return { 'custom': `所選月份已執行月結，無法新增單據` };
    }

    return null;
  };
}

/**
 * 開帳日檢查驗證器
 * 檢查選擇的日期不可早於開帳日
 * @param openDate 總庫開帳日 (null 表示總庫尚未開帳，不擋(有其他件查))
 */
export function openDateValidator(openDate: Date | null): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value || !openDate) {
      return null;
    }

    const selected = new Date(control.value);
    const selectedDay = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());

    const open = new Date(openDate);
    const openDay = new Date(open.getFullYear(), open.getMonth(), open.getDate());

    if (selectedDay < openDay) {
      const rocStr = `${openDay.getFullYear() - 1911}/${(openDay.getMonth() + 1).toString().padStart(2, '0')}/${openDay.getDate().toString().padStart(2, '0')}`;
      return { 'custom': `日期不可早於開帳日 ${rocStr}` };
    }

    return null;
  };
}

/**
 * 判斷日期是否晚於今天（純函式，只比日期不比時間）
 * @param date 要檢查的日期
 * @returns true 表示晚於今天（未來日期）
 */
export function isFutureDate(date: Date | null): boolean {
  if (!date) {
    return false;
  }
  const selected = new Date(date);
  const selectedDay = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return selectedDay > today;
}
